import React, { useEffect, useMemo, useRef, useState } from "react";
import { Badge, Button, Card, Col, ProgressBar, Row, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { DragDropContext } from "@hello-pangea/dnd";
import Countdown from "react-countdown";
import ReactTooltip from "react-tooltip";
import swal from "sweetalert";
import {
  fetchRetroBoards,
  fetchRetroBoardDetail,
  resetRetroCurrentBoard,
  createRetroBoard,
  updateRetroBoard,
  closeRetroBoard,
  reopenRetroBoard,
  startRetroTimer,
  deleteRetroBoard,
  createRetroCard,
  updateRetroCard,
  deleteRetroCard,
  reorderRetroCards,
  addRetroVote,
  removeRetroVote,
  toggleRetroLike,
} from "../../redux";
import { getRetroErrorCode } from "../../services/retroBoard";
import { RETRO_VOTES_ENABLED } from "./retroConfig";
import RetroColumn from "./RetroColumn";
import RetroCardModal from "./RetroCardModal";
import RetroBoardModal from "./RetroBoardModal";
import planningImg from "../../assets/planning-1.png";
import "./RetroBoard.css";

// Countdown renderer minutos:segundos (RF-27) — cuando termina, react-countdown ya invocó onComplete
// (refetch) antes de este render "completed", así que no hace falta pintar nada especial aquí.
const countdownRenderer = ({ hours, minutes, seconds, completed }) => {
  if (completed) return null;
  const totalMinutes = hours * 60 + minutes;
  const pad = (n) => String(n).padStart(2, "0");
  return (
    <span className="retro-timer-countdown fw-bold">
      {pad(totalMinutes)}:{pad(seconds)}
    </span>
  );
};

// Contenedor del "Tablero de Retrospectiva colaborativo" (spec 006). Molde de integración: mismo patrón
// que AprendizajesBriefcase/RetrospectivasBriefcase (tab de EditBriefcase.jsx, prop `proyectos` del
// portafolio para el selector de Ciclo/Sprint, PA-2 resuelta). Dos modos, ambos dentro de este único
// componente (no se abrió un archivo de "lista" aparte: el listado de tableros es simple y vive natural
// aquí, ver plan.md §3.3):
//  - Lista de tableros del portafolio (RF-5): grid de "boards" estilo Reetro, alta/edición/cierre/borrado.
//  - Detalle de un tablero: 3 lanes (columnas del catálogo) con @hello-pangea/dnd para el reorder vertical
//    dentro de columna (RF-10); cross-column deshabilitado (FA-3, ver handleDragEnd). v1 SIN tiempo real:
//    updates optimistas + rollback en error (RF-16, redux/retroBoard/retroBoardActions.js).
//
// Props:
//  - idPortafolio: string (IdPortafolio del padre, EditBriefcase.jsx)
//  - proyectos: [{ IdProyecto, Nombre }] (para el selector de Ciclo/Sprint del modal de tablero)
const RetroBoard = ({ idPortafolio, proyectos = [] }) => {
  const [t] = useTranslation("retroBoard");
  const dispatch = useDispatch();
  const { boards, currentBoard, loading, error } = useSelector((state) => state.retroBoard);

  const currentUserId = localStorage.getItem("UID");

  const [selectedBoardId, setSelectedBoardId] = useState(null);
  const [showBoardModal, setShowBoardModal] = useState(false);
  const [editingBoard, setEditingBoard] = useState(null);
  const [savingBoard, setSavingBoard] = useState(false);

  const [showCardModal, setShowCardModal] = useState(false);
  const [cardTargetColumn, setCardTargetColumn] = useState(null);
  const [editingCard, setEditingCard] = useState(null);
  const [savingCard, setSavingCard] = useState(false);

  useEffect(() => {
    if (idPortafolio) {
      dispatch(fetchRetroBoards(idPortafolio));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idPortafolio]);

  useEffect(() => {
    if (idPortafolio && selectedBoardId) {
      dispatch(fetchRetroBoardDetail(idPortafolio, selectedBoardId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idPortafolio, selectedBoardId]);

  useEffect(() => {
    ReactTooltip.rebuild();
  });

  const tablero = currentBoard.tablero;
  const readOnly = tablero ? tablero.Estado === "CERRADO" : false;

  // RF-23/CA-16 (incremento post-cierre) + RF-25/CA-18 (Incremento 2): gate de UX compartido por
  // "Reabrir" e "Iniciar retrospectiva" — visible solo si el usuario actual es el CreadoPor del tablero o
  // el Gerente del proyecto/ciclo asociado. La prop `proyectos` SÍ trae `Gerente`
  // (SP_PORTAFOLIOS_DINAMICOS_DETALLE), así que el gate queda completo en el front; el enforcement real
  // (defensa en profundidad) está en el SP (SP_RETRO_TABLERO_ABRIR / SP_RETRO_TIMER_INICIAR).
  const proyectoAsociado = tablero ? proyectos.find((p) => p.IdProyecto === tablero.IdProyecto) : null;
  const canManageCeremony =
    !!tablero &&
    !!currentUserId &&
    (tablero.CreadoPor === currentUserId ||
      (proyectoAsociado && proyectoAsociado.Gerente === currentUserId));

  // RF-25/RF-26/RF-27 (Incremento 2): fin de la ceremonia calculado en el front SOLO para pintar el
  // countdown y decidir la UX de escritura — la autoridad real (vencimiento + auto-cierre + reveal) vive
  // en el servidor (SP), este cálculo nunca gobierna el 409 TIMER_NOT_RUNNING.
  const ceremonyEndMs =
    tablero && tablero.TimerInicioUtc
      ? new Date(tablero.TimerInicioUtc).getTime() + (tablero.TimerDuracionMinutos || 0) * 60000
      : null;
  const timerRunning =
    !!tablero &&
    tablero.Estado === "ABIERTO" &&
    !!tablero.TimerInicioUtc &&
    !!ceremonyEndMs &&
    Date.now() < ceremonyEndMs;
  // RF-26 (UX): la escritura (agregar/editar/eliminar/reordenar) solo tiene sentido con el timer corriendo.
  const writingEnabled = timerRunning;
  const showStartTimerBtn =
    !!tablero && tablero.Estado === "ABIERTO" && !tablero.TimerInicioUtc && canManageCeremony;

  const misVotosTotalEnTablero = useMemo(
    () => (currentBoard.tarjetas || []).reduce((acc, c) => acc + (c.MisVotos || 0), 0),
    [currentBoard.tarjetas]
  );
  // D8-3/RF-29: con el flag off no se calcula/usa para deshabilitar nada visible (la UI de votos está oculta).
  const remainingBudget =
    RETRO_VOTES_ENABLED && tablero ? Math.max(0, tablero.MaxVotosPorUsuario - misVotosTotalEnTablero) : 0;

  const votantesPorTarjeta = useMemo(() => {
    const map = {};
    (currentBoard.votantes || []).forEach((v) => {
      if (!map[v.IdTarjeta]) map[v.IdTarjeta] = { votos: [], likes: [] };
      const bucket = v.Tipo === "LIKE" ? "likes" : "votos";
      if (v.Nombre) map[v.IdTarjeta][bucket].push(v.Nombre);
    });
    return map;
  }, [currentBoard.votantes]);

  const tarjetasPorColumna = (idColumna) =>
    (currentBoard.tarjetas || [])
      .filter((c) => c.IdColumna === idColumna)
      .sort((a, b) => (a.Orden || 0) - (b.Orden || 0));

  // ------------------------------------------------------------------------------------------------
  // Tableros (lista)
  // ------------------------------------------------------------------------------------------------

  const openCreateBoard = () => {
    setEditingBoard(null);
    setShowBoardModal(true);
  };

  const openEditBoard = (b) => {
    setEditingBoard(b);
    setShowBoardModal(true);
  };

  const handleSaveBoard = async (data) => {
    try {
      setSavingBoard(true);
      if (editingBoard) {
        await dispatch(updateRetroBoard(idPortafolio, editingBoard.IdTablero, data));
      } else {
        await dispatch(createRetroBoard(idPortafolio, data));
      }
      swal(
        t("retro_done_title"),
        editingBoard ? t("retro_done_update") : t("retro_done_create"),
        "success"
      );
      setShowBoardModal(false);
      setEditingBoard(null);
    } catch (e) {
      swal(t("retro_error_title"), t("retro_error_save"), "error");
    } finally {
      setSavingBoard(false);
    }
  };

  const handleCloseBoard = (b) => {
    swal({
      title: t("retro_swal_close_title"),
      text: t("retro_swal_close_text"),
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (confirm) => {
      if (!confirm) return;
      try {
        await dispatch(closeRetroBoard(idPortafolio, b.IdTablero));
        swal(t("retro_done_title"), t("retro_done_close"), "success");
      } catch (e) {
        swal(t("retro_error_title"), t("retro_error_generic"), "error");
      }
    });
  };

  // RF-23/CA-16: reabrir un tablero cerrado. El botón solo se muestra a quien pasa el gate de UX
  // (canManageCeremony); el 403 NOT_ALLOWED_REOPEN se maneja igual (defensa en profundidad del SP).
  const handleReopenBoard = (b) => {
    swal({
      title: t("retro_board_reopen"),
      text: t("retro_board_reopen_confirm"),
      icon: "warning",
      buttons: true,
    }).then(async (confirm) => {
      if (!confirm) return;
      try {
        await dispatch(reopenRetroBoard(idPortafolio, b.IdTablero));
        swal(t("retro_done_title"), t("retro_done_reopen"), "success");
      } catch (e) {
        const code = getRetroErrorCode(e);
        if (code === "NOT_ALLOWED_REOPEN") {
          swal(t("retro_error_title"), t("retro_error_not_allowed_reopen"), "warning");
        } else {
          swal(t("retro_error_title"), t("retro_error_generic"), "error");
        }
      }
    });
  };

  // RF-25/CA-18 (Incremento 2): inicia la ceremonia. El botón solo se muestra a quien pasa el gate de UX
  // (canManageCeremony, mismo cálculo que reabrir); el 403 NOT_ALLOWED_START_TIMER y el 409 BOARD_CLOSED
  // se manejan igual (defensa en profundidad del SP SP_RETRO_TIMER_INICIAR).
  const handleStartTimer = async () => {
    if (!tablero) return;
    try {
      await dispatch(startRetroTimer(idPortafolio, tablero.IdTablero));
    } catch (e) {
      const code = getRetroErrorCode(e);
      if (code === "NOT_ALLOWED_START_TIMER") {
        swal(t("retro_error_title"), t("retro_error_not_allowed_start_timer"), "warning");
      } else if (code === "BOARD_CLOSED") {
        swal(t("retro_error_title"), t("retro_error_board_closed"), "warning");
      } else {
        swal(t("retro_error_title"), t("retro_error_generic"), "error");
      }
    }
  };

  // RF-27: el vencimiento es autoridad del servidor; el countdown del front solo dispara el refetch, que
  // trae el auto-cierre + reveal ya resueltos por el SP.
  // Guarda anti-ráfaga: cada refetch resetea `tablero` a null (LOADING_) y remonta el <Countdown> desde
  // cero; si el reloj del cliente va adelantado, ese <Countdown> nace ya vencido y volvería a disparar
  // onComplete en bucle hasta que el reloj del server alcance. Disparamos una sola vez por instancia de
  // timer (misma ceremonyEndMs) y nunca si ya hay una carga en curso.
  const countdownFiredRef = useRef(null);
  const handleCountdownComplete = () => {
    if (!idPortafolio || !selectedBoardId) return;
    if (loading || countdownFiredRef.current === ceremonyEndMs) return;
    countdownFiredRef.current = ceremonyEndMs;
    dispatch(fetchRetroBoardDetail(idPortafolio, selectedBoardId));
  };

  const handleDeleteBoard = (b) => {
    swal({
      title: t("retro_swal_delete_title"),
      text: t("retro_swal_delete_text"),
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (confirm) => {
      if (!confirm) return;
      try {
        await dispatch(deleteRetroBoard(idPortafolio, b.IdTablero));
        swal(t("retro_done_title"), t("retro_done_delete"), "success");
        if (selectedBoardId === b.IdTablero) {
          setSelectedBoardId(null);
          dispatch(resetRetroCurrentBoard());
        }
      } catch (e) {
        swal(t("retro_error_title"), t("retro_error_delete"), "error");
      }
    });
  };

  const openBoard = (idTablero) => setSelectedBoardId(idTablero);
  const backToList = () => {
    setSelectedBoardId(null);
    dispatch(resetRetroCurrentBoard());
  };

  // ------------------------------------------------------------------------------------------------
  // Tarjetas
  // ------------------------------------------------------------------------------------------------

  const showBusinessError = (e, fallbackKey) => {
    const code = getRetroErrorCode(e);
    if (code === "NO_VOTE_BUDGET") {
      swal(t("retro_error_title"), t("retro_error_no_vote_budget"), "warning");
    } else if (code === "BOARD_CLOSED") {
      swal(t("retro_error_title"), t("retro_error_board_closed"), "warning");
    } else if (code === "TIMER_NOT_RUNNING") {
      swal(t("retro_error_title"), t("retro_error_timer_not_running"), "warning");
    } else {
      swal(t("retro_error_title"), t(fallbackKey), "error");
    }
  };

  const openAddCard = (columna) => {
    setCardTargetColumn(columna);
    setEditingCard(null);
    setShowCardModal(true);
  };

  const openEditCard = (tarjeta) => {
    setCardTargetColumn(null);
    setEditingCard(tarjeta);
    setShowCardModal(true);
  };

  const handleSaveCard = async (texto) => {
    try {
      setSavingCard(true);
      if (editingCard) {
        await dispatch(
          updateRetroCard(idPortafolio, selectedBoardId, editingCard.IdTarjeta, texto, currentUserId)
        );
      } else {
        await dispatch(
          createRetroCard(
            idPortafolio,
            selectedBoardId,
            cardTargetColumn.IdColumna,
            cardTargetColumn.Codigo,
            texto,
            currentUserId
          )
        );
      }
      setShowCardModal(false);
      setEditingCard(null);
      setCardTargetColumn(null);
    } catch (e) {
      showBusinessError(e, "retro_error_save");
    } finally {
      setSavingCard(false);
    }
  };

  const handleDeleteCard = (tarjeta) => {
    swal({
      title: t("retro_card_swal_delete_title"),
      text: t("retro_card_swal_delete_text"),
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (confirm) => {
      if (!confirm) return;
      dispatch(deleteRetroCard(idPortafolio, selectedBoardId, tarjeta.IdTarjeta)).catch((e) =>
        showBusinessError(e, "retro_error_delete")
      );
    });
  };

  const handleVoteAdd = (tarjeta) => {
    if (readOnly || remainingBudget <= 0) return;
    dispatch(addRetroVote(idPortafolio, selectedBoardId, tarjeta.IdTarjeta)).catch((e) =>
      showBusinessError(e, "retro_error_generic")
    );
  };

  const handleVoteRemove = (tarjeta) => {
    if (readOnly) return;
    dispatch(removeRetroVote(idPortafolio, selectedBoardId, tarjeta.IdTarjeta)).catch((e) =>
      showBusinessError(e, "retro_error_generic")
    );
  };

  const handleLikeToggle = (tarjeta) => {
    if (readOnly) return;
    dispatch(toggleRetroLike(idPortafolio, selectedBoardId, tarjeta.IdTarjeta)).catch((e) =>
      showBusinessError(e, "retro_error_generic")
    );
  };

  // RF-10/FA-3: reorder SIEMPRE vertical dentro de la misma columna; si el drop cae en otra columna (o
  // fuera de cualquier Droppable) se descarta sin llamar al backend — el propio DragDropContext ya
  // "regresa" la tarjeta a su posición porque el estado de Redux no cambió.
  const handleDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) return;
    if (destination.droppableId !== source.droppableId) return; // cross-column deshabilitado (FA-3)
    if (destination.index === source.index) return;
    // Reorder = priorización, disponible siempre que el tablero esté ABIERTO (igual que los likes), NO atado
    // al timer: en la fase de priorización/discusión (reabierto, timer sin iniciar) se ordena para priorizar.
    // Crear/editar/eliminar tarjeta sí siguen atados al timer (writingEnabled). El SP solo exige ABIERTO.
    if (readOnly) return;

    const idColumna = source.droppableId;
    const cards = tarjetasPorColumna(idColumna);
    const reordered = Array.from(cards);
    const [moved] = reordered.splice(source.index, 1);
    reordered.splice(destination.index, 0, moved);

    dispatch(reorderRetroCards(idPortafolio, selectedBoardId, idColumna, reordered)).catch((e) =>
      showBusinessError(e, "retro_error_generic")
    );
  };

  // ------------------------------------------------------------------------------------------------
  // Render — lista de tableros
  // ------------------------------------------------------------------------------------------------

  if (!selectedBoardId) {
    const items = boards || [];
    return (
      <div>
        {loading ? (
          <div className="d-flex justify-content-center py-4">
            <Spinner animation="border" variant="danger" />
          </div>
        ) : error ? (
          <p className="text-center text-muted py-4">{t("retro_error_load")}</p>
        ) : items.length > 0 ? (
          <Row>
            {items.map((b) => (
              <Col md={4} className="mb-3" key={b.IdTablero}>
                <Card className="shadow-sm border-0 h-100 retro-board-card">
                  <Card.Body className="d-flex flex-column">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <h6 className="mb-0 fw-bold text-truncate" title={b.Titulo}>
                        {b.Titulo}
                      </h6>
                      <Badge bg={b.Estado === "CERRADO" ? "secondary" : "success"}>
                        {b.Estado === "CERRADO" ? t("retro_estado_cerrado") : t("retro_estado_abierto")}
                      </Badge>
                    </div>
                    <p className="text-muted small mb-3">{b.NombreProyecto}</p>
                    <div className="d-flex text-muted small mb-3" style={{ gap: "12px" }}>
                      <span>
                        <i className="fa fa-sticky-note mr-1" />
                        {b.TotalTarjetas || 0}
                      </span>
                      <span>
                        <i className="fa fa-check-circle mr-1" />
                        {b.TotalVotos || 0}
                      </span>
                      <span>
                        <i className="fa fa-heart mr-1" />
                        {b.TotalLikes || 0}
                      </span>
                    </div>
                    <div className="mt-auto d-flex" style={{ gap: "6px" }}>
                      <Button size="sm" variant="primary" className="flex-grow-1" onClick={() => openBoard(b.IdTablero)}>
                        {t("retro_btn_open")}
                      </Button>
                      <Button size="sm" variant="outline-secondary" onClick={() => openEditBoard(b)} title={t("retro_btn_edit")}>
                        <i className="fa fa-pen" />
                      </Button>
                      {b.Estado !== "CERRADO" && (
                        <Button
                          size="sm"
                          variant="outline-warning"
                          onClick={() => handleCloseBoard(b)}
                          title={t("retro_btn_close")}
                        >
                          <i className="fa fa-lock" />
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => handleDeleteBoard(b)}
                        title={t("retro_btn_delete")}
                      >
                        <i className="fa fa-trash" />
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        ) : (
          <div className="text-center pt-4 mt-4">
            <img src={planningImg} alt="" className="img-fluid mb-3 animated pulse slower infinite" width="200" />
            <h5>{t("retro_boards_empty_title")}</h5>
            <p>{t("retro_boards_empty_desc")}</p>
            <Button onClick={openCreateBoard}>
              <i className="fa fa-plus-circle mr-2" />
              {t("retro_boards_btn_create")}
            </Button>
          </div>
        )}

        {items.length > 0 && (
          <Button
            variant="success"
            className="position-fixed shadow animate__animated animate__fadeInUp"
            style={{ bottom: "1rem", right: "1rem" }}
            onClick={openCreateBoard}
          >
            <i className="fa fa-plus-circle mr-2" />
            {t("retro_boards_btn_create")}
          </Button>
        )}

        <RetroBoardModal
          show={showBoardModal}
          onHide={() => setShowBoardModal(false)}
          proyectos={proyectos}
          initialData={editingBoard}
          saving={savingBoard}
          onSave={handleSaveBoard}
          t={t}
        />
      </div>
    );
  }

  // ------------------------------------------------------------------------------------------------
  // Render — detalle del tablero (3 columnas, RF-6)
  // ------------------------------------------------------------------------------------------------

  return (
    <div className="retro-board-detail">
      {/* No se monta un <ReactTooltip> local: el global de Layout (MakeTooltips) ya captura todos los
          data-tip del documento. Montar otra instancia hacía que cada tooltip saliera DUPLICADO
          (pwc blanco + default oscuro). ReactTooltip.rebuild() del useEffect registra los nodos nuevos. */}
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap" style={{ gap: "8px" }}>
        <div className="d-flex align-items-center" style={{ gap: "10px" }}>
          <Button variant="light" size="sm" onClick={backToList}>
            <i className="fa fa-arrow-left mr-1" />
            {t("retro_back_to_list")}
          </Button>
          <h5 className="mb-0 fw-bold">{tablero ? tablero.Titulo : ""}</h5>
          {tablero && (
            <Badge bg={readOnly ? "secondary" : "success"}>
              {readOnly ? t("retro_estado_cerrado") : t("retro_estado_abierto")}
            </Badge>
          )}
          {/* RF-27/RF-28: etiqueta de fase — "Escribiendo…" con el timer corriendo (brainstorm privado),
              "Reveladas" una vez que el tablero se cerró habiendo tenido timer (reveal ya ocurrió). */}
          {timerRunning && (
            <Badge bg="info" className="retro-timer-phase">
              {t("retro_timer_phase_writing")}
            </Badge>
          )}
          {readOnly && tablero && tablero.TimerInicioUtc && (
            <Badge bg="light" text="dark" className="retro-timer-phase">
              {t("retro_timer_phase_revealed")}
            </Badge>
          )}
        </div>
        <div className="d-flex align-items-center" style={{ gap: "10px" }}>
          {/* RF-27: countdown visible mientras el timer corre; al vencer, onComplete refetch trae el
              auto-cierre + reveal resueltos por el servidor (autoridad real). */}
          {timerRunning && (
            <div className="text-end">
              <small className="text-muted d-block">{t("retro_timer_label")}</small>
              <Countdown
                date={ceremonyEndMs}
                renderer={countdownRenderer}
                onComplete={handleCountdownComplete}
              />
            </div>
          )}
          {RETRO_VOTES_ENABLED && tablero && (
            <div className="text-end" style={{ minWidth: "160px" }}>
              <small className="text-muted d-block">
                {t("retro_vote_budget_label", {
                  restantes: remainingBudget,
                  total: tablero.MaxVotosPorUsuario,
                })}
              </small>
              <ProgressBar
                now={tablero.MaxVotosPorUsuario ? (remainingBudget / tablero.MaxVotosPorUsuario) * 100 : 0}
                variant={remainingBudget > 0 ? "primary" : "secondary"}
                style={{ height: "6px" }}
              />
            </div>
          )}
          {/* RF-25/CA-18: "Iniciar retrospectiva" — solo visible al creador/Gerente y antes de que el
              timer haya iniciado. */}
          {showStartTimerBtn && (
            <Button variant="outline-primary" size="sm" onClick={handleStartTimer}>
              <i className="fa fa-play mr-1" />
              {t("retro_btn_start_timer")}
            </Button>
          )}
          {tablero && !readOnly && (
            <Button variant="outline-warning" size="sm" onClick={() => handleCloseBoard(tablero)}>
              <i className="fa fa-lock mr-1" />
              {t("retro_header_btn_close_retro")}
            </Button>
          )}
          {tablero && readOnly && canManageCeremony && (
            <Button variant="outline-success" size="sm" onClick={() => handleReopenBoard(tablero)}>
              <i className="fa fa-unlock mr-1" />
              {t("retro_board_reopen")}
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="d-flex justify-content-center py-4">
          <Spinner animation="border" variant="danger" />
        </div>
      ) : error ? (
        <p className="text-center text-muted py-4">{t("retro_error_load")}</p>
      ) : (
        <>
          {/* RF-22: estado vacío ilustrado cuando el tablero todavía no tiene ninguna tarjeta. */}
          {(currentBoard.tarjetas || []).length === 0 && (currentBoard.columnas || []).length > 0 && (
            <div className="text-center pt-3 mb-4">
              <img src={planningImg} alt="" className="img-fluid mb-2 animated pulse slower infinite" width="160" />
              <h6>{t("retro_empty_board_title")}</h6>
              <p className="text-muted">{t("retro_empty_board_desc")}</p>
            </div>
          )}
          <DragDropContext onDragEnd={handleDragEnd}>
            <Row>
              {(currentBoard.columnas || []).map((columna) => (
                <Col md={4} key={columna.IdColumna} className="mb-3">
                  <RetroColumn
                    columna={columna}
                    tarjetas={tarjetasPorColumna(columna.IdColumna)}
                    votantesPorTarjeta={votantesPorTarjeta}
                    readOnly={readOnly}
                    writingEnabled={writingEnabled}
                    remainingBudget={remainingBudget}
                    onAddCard={openAddCard}
                    onEditCard={openEditCard}
                    onDeleteCard={handleDeleteCard}
                    onVoteAdd={handleVoteAdd}
                    onVoteRemove={handleVoteRemove}
                    onLikeToggle={handleLikeToggle}
                    t={t}
                  />
                </Col>
              ))}
            </Row>
          </DragDropContext>
        </>
      )}

      <RetroCardModal
        show={showCardModal}
        onHide={() => {
          setShowCardModal(false);
          setEditingCard(null);
          setCardTargetColumn(null);
        }}
        initialText={editingCard ? editingCard.Texto : ""}
        editing={!!editingCard}
        saving={savingCard}
        onSave={handleSaveCard}
        t={t}
      />
    </div>
  );
};

export default RetroBoard;
